import { FC, useCallback } from "react";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import FunctionsIcon from "@mui/icons-material/Functions";
import {
  capitalize,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ResourceExplorerChartScale } from "@/store/resource-explorer/utils/types/billingExplorer";

interface ChartScaleSelectProps {
  scale: ResourceExplorerChartScale;
  onScaleChange(scale: ResourceExplorerChartScale): void;
}

export const ChartScaleSelect: FC<ChartScaleSelectProps> = ({
  scale,
  onScaleChange,
}) => {
  const changeHandler = useCallback(
    (event: SelectChangeEvent<"linear" | "logarithmic">) => {
      const val = event.target.value as ResourceExplorerChartScale;

      if (!val || val === scale) {
        return;
      }

      onScaleChange(val);
    },
    [onScaleChange, scale],
  );

  return (
    <FormControl size="small" fullWidth>
      <InputLabel>Scale</InputLabel>

      <Select
        value={scale}
        label="Scale"
        size="small"
        sx={selectStyles}
        MenuProps={selectMenuProps}
        onChange={changeHandler}
        startAdornment={icons[scale]}
        renderValue={(value) => (
          <Typography pl={1}>{capitalize(value)}</Typography>
        )}
      >
        <MenuItem value="linear">
          <ListItemIcon>{icons.linear}</ListItemIcon>
          <ListItemText>Linear</ListItemText>
        </MenuItem>

        <MenuItem value="logarithmic">
          <ListItemIcon>{icons.logarithmic}</ListItemIcon>
          <ListItemText sx={{ alignItems: "center" }}>
            Logarithmic
            <Tooltip
              arrow
              placement="top"
              title="Zero or negative values will not be displayed on a logarithmic scale."
            >
              <InfoOutlinedIcon
                color="action"
                fontSize="small"
                sx={{ verticalAlign: "middle", ml: 2 }}
              />
            </Tooltip>
          </ListItemText>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

const icons = {
  linear: <StackedLineChartIcon fontSize="small" color="action" />,
  logarithmic: <FunctionsIcon fontSize="small" color="action" />,
};

const selectMenuProps: SelectProps["MenuProps"] = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: 5, horizontal: "left" },
};

const selectStyles = {
  bgcolor: "white",
  "& .MuiSelect-select": { px: 0.5 },
};
